import { Colors, Lightning, Router, Storage } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../components/input/Button/Button";
import theme from "../../lib/theme";
import { isPostTvLinux, isPostTvAndroid, STORAGE_KEYS } from "../../lib/utils";
import { postTvWebSocket } from "../../lib/websocket";

// Module-level storage for pending callbacks (replaces Storage usage)
let pendingCallbacks: { onSuccess?: () => void; onCancel?: () => void } | null = null;

export function setPinInputCallbacks(onSuccess?: () => void, onCancel?: () => void) {
    pendingCallbacks = { onSuccess, onCancel };
}

export function clearPinInputCallbacks() {
    pendingCallbacks = null;
}

// PinBox component for individual PIN input boxes
interface PinBoxTemplateSpec extends Lightning.Component.TemplateSpec {
    Background?: object;
    Mask?: object;
    Number?: object;
}

class PinBox
    extends Lightning.Component<PinBoxTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PinBoxTemplateSpec>
{
    override _construct() {}

    static override _template(): Lightning.Component.Template<PinBoxTemplateSpec> {
        const boxSize = 80;
        return {
            w: boxSize,
            h: boxSize,
            Background: {
                w: boxSize,
                h: boxSize,
                rect: true,
                color: Colors(theme.colors.bg.secondary).get(),
                shader: {
                    type: Lightning.shaders.RoundedRectangle,
                    radius: 8,
                    stroke: 2,
                    strokeColor: Colors(theme.colors.focus).get()
                }
            },
            Mask: {
                x: boxSize / 2,
                y: boxSize / 2,
                mount: 0.5,
                text: {
                    text: "•",
                    fontSize: 48,
                    textColor: Colors(theme.colors.text.primary).get(),
                    textAlign: "center",
                    shadow: false,
                    shadowColor: 0x00000000
                },
                alpha: 0
            },
            Number: {
                x: boxSize / 2,
                y: boxSize / 2,
                mount: 0.5,
                text: {
                    text: "",
                    fontSize: 40,
                    textColor: Colors(theme.colors.text.primary).get(),
                    textAlign: "center"
                },
                alpha: 0
            }
        };
    }

    override _focus() {
        (this.getByRef("Background") as any).patch({
            shader: {
                type: Lightning.shaders.RoundedRectangle,
                radius: 8,
                stroke: 5,
                strokeColor: Colors(theme.colors.focus).get()
            }
        });
    }

    override _unfocus() {
        (this.getByRef("Background") as any).patch({
            shader: {
                type: Lightning.shaders.RoundedRectangle,
                radius: 8,
                stroke: 2,
                strokeColor: Colors(theme.colors.focus).get()
            }
        });
    }
}

interface PinInputTemplateSpec extends Lightning.Component.TemplateSpec {
    Box: {
        Background: object;
        Content: {
            Title: typeof TranslatableText;
            PinBoxes: {
                Box1: typeof PinBox;
                Box2: typeof PinBox;
                Box3: typeof PinBox;
                Box4: typeof PinBox;
            };
            Options?: {
                ChangePin: typeof Button;
                TurnOff: typeof Button;
            };
            Error: typeof TranslatableText;
            Actions: {
                Cancel: typeof Button;
                Submit: typeof Button;
            };
        };
    };
}

export class PinInput
    extends Lightning.Component<PinInputTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PinInputTemplateSpec>
{
    private _pin: string[] = [];
    private _focusedBoxIndex: number = 0;
    private _onSuccess?: () => void;
    private _onCancel?: () => void;
    private _isVerifying: boolean = false;
    private _mode: "verify" | "set" | "confirm" | "age" | "options" = "verify";
    private _onPinSet?: (pin: string) => void;
    private _onTurnOff?: () => void; // Callback for turning off parental control
    private _firstPin?: string; // Store first PIN for confirmation
    private _digitCount: number = 4; // Number of digits (4 for PIN, 2 for age)

    readonly Box = this.getByRef("Box")!;
    readonly Content = this.Box.getByRef("Content")!;
    readonly PinBoxes = this.Content.getByRef("PinBoxes")!;
    readonly Error = this.Content.getByRef("Error")!;
    readonly Actions = this.Content.getByRef("Actions")!;
    readonly Cancel = this.Actions.getByRef("Cancel")!;
    readonly Submit = this.Actions.getByRef("Submit")!;
    readonly Title = this.Content.getByRef("Title")!;
    readonly Options = this.Content.getByRef("Options") as any;
    readonly ChangePin = this.Options?.getByRef?.("ChangePin");
    readonly TurnOff = this.Options?.getByRef?.("TurnOff");

    static override _template(): Lightning.Component.Template<PinInputTemplateSpec> {
        const boxSize = 80;
        const boxSpacing = 20;
        const totalWidth4 = boxSize * 4 + boxSpacing * 3;
        const totalWidth2 = boxSize * 2 + boxSpacing * 1;

        return {
            zIndex: 50,
            w: theme.layout.w,
            h: theme.layout.h,
            x: 0,
            y: 0,
            rect: true,
            color: Colors(theme.colors.bg.overlay).alpha(0.7).get(),
            alpha: 0,
            collision: true,
            Box: {
                w: 628,
                h: 400,
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                alpha: 0,
                Background: {
                    x: -1,
                    y: -1,
                    texture: Lightning.Tools.getRoundRect(
                        628,
                        400,
                        12,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.secondary).get()
                    )
                },
                Content: {
                    w: (w) => w,
                    h: (h) => h,
                    flex: {
                        direction: "column",
                        justifyContent: "center",
                        alignItems: "center"
                        // paddingLeft: 24,
                        // paddingRight: 24
                    },
                    Title: {
                        flexItem: { marginBottom: 40 },
                        type: TranslatableText,
                        key: "pin.title",
                        text: {
                            textAlign: "center",
                            fontFace: "SemiBold",
                            fontSize: 28,
                            wordWrap: true,
                            wordWrapWidth: 400
                        }
                    },
                    PinBoxes: {
                        flexItem: { marginBottom: 20 },
                        flex: {
                            direction: "row",
                            alignItems: "center",
                            justifyContent: "center"
                        },
                        w: totalWidth4, // Will be updated dynamically based on digit count
                        Box1: {
                            type: PinBox,
                            flexItem: { marginRight: boxSpacing }
                        },
                        Box2: {
                            type: PinBox,
                            flexItem: { marginRight: boxSpacing }
                        },
                        Box3: {
                            type: PinBox,
                            flexItem: { marginRight: boxSpacing }
                        },
                        Box4: {
                            type: PinBox
                        }
                    },
                    Options: {
                        flexItem: { marginBottom: 20 },
                        collision: true,
                        flex: {
                            direction: "column",
                            alignItems: "center",
                            justifyContent: "center"
                        },
                        w: 440,
                        alpha: 0,
                        visible: false,
                        ChangePin: {
                            type: Button,
                            w: 400,
                            label: "settings.changePin",
                            signals: {
                                handleClick: "_handleChangePin"
                            }
                        },
                        TurnOff: {
                            type: Button,
                            w: 400,
                            label: "settings.turnOffParentalControl",
                            signals: {
                                handleClick: "_handleTurnOff"
                            }
                        }
                    },
                    Error: {
                        // flexItem: { marginBottom: 20 },
                        type: TranslatableText,
                        // mountX: 0.5,
                        // w: (w) => w,
                        key: "pin.enter",
                        h: 30,
                        text: {
                            textAlign: "center",
                            text: "",
                            fontSize: 20,
                            textColor: Colors("#ff0000").get()
                        },
                        alpha: 0
                    },
                    Actions: {
                        flexItem: { marginTop: 20 },
                        collision: true,
                        flex: {
                            direction: "row",
                            alignItems: "flex-start",
                            justifyContent: "space-between"
                        },
                        w: 440,
                        Cancel: {
                            type: Button,
                            w: 200,
                            label: "pin.cancel"
                        },
                        Submit: {
                            type: Button,
                            w: 200,
                            label: "pin.submit"
                        }
                    }
                }
            }
        };
    }

    override _init() {
        this._updatePinBoxes();
        // Initialize Options visibility
        if (this.Options) {
            if (this.Options) (this.Options as any).patch({ alpha: 0, visible: false });
        }
        this._setState("PinBoxesState");
    }

    override _active() {
        this._setState("PinBoxesState");
        // Auto-open if there are pending callbacks
        if (pendingCallbacks) {
            this.open();
        }
    }

    static override _states() {
        return [
            class OptionsState extends this {
                private _optionIndex: number = 0; // 0 = ChangePin, 1 = TurnOff

                override $enter() {
                    this._optionIndex = 0;
                    // Ensure Options are visible when entering this state
                    if (this.Options) {
                        (this.Options as any).patch({ alpha: 1, visible: true });
                    }
                    if (this.ChangePin) {
                        this.ChangePin.patch({ alpha: 1, visible: true });
                    }
                    if (this.TurnOff) {
                        this.TurnOff.patch({ alpha: 1, visible: true });
                    }
                }

                override _getFocused() {
                    if (this._optionIndex === 0 && this.ChangePin) {
                        return this.ChangePin;
                    } else if (this._optionIndex === 1 && this.TurnOff) {
                        return this.TurnOff;
                    }
                    return this.ChangePin || null;
                }

                override _handleDown() {
                    if (this._optionIndex < 1) {
                        this._optionIndex = 1;
                        this._refocus();
                        return true;
                    }
                    return false;
                }

                override _handleUp() {
                    if (this._optionIndex > 0) {
                        this._optionIndex = 0;
                        this._refocus();
                        return true;
                    }
                    return false;
                }

                override _handleEnter() {
                    if (this._optionIndex === 0 && this.ChangePin) {
                        this._handleChangePin();
                        return true;
                    } else if (this._optionIndex === 1 && this.TurnOff) {
                        this._handleTurnOff();
                        return true;
                    }
                    return false;
                }

                override _handleRight() {
                    // Options are vertical, prevent navigation
                    return false;
                }

                override _handleLeft() {
                    // Options are vertical, prevent navigation
                    return false;
                }
            },
            class PinBoxesState extends this {
                override _getFocused() {
                    return this.PinBoxes.getByRef(`Box${this._focusedBoxIndex + 1}` as any);
                }

                override _handleDown() {
                    if (this._isVerifying) return false;
                    // Allow focusing Cancel button even when PIN is not complete
                    this._setState("CancelState");
                    return true;
                }

                override _handleKey(event: KeyboardEvent) {
                    console.log(
                        "PIN HANDLE KEY",
                        this._isVerifying,
                        "mode:",
                        this._mode,
                        "pin length:",
                        this._pin.length,
                        "digitCount:",
                        this._digitCount
                    );
                    if (this._isVerifying) return false;

                    const key = event.key;

                    // Handle number keys - convert to number and check if it's a valid digit
                    const numKey = +key;
                    const maxLength = this._mode === "age" ? this._digitCount : this._digitCount;
                    if (!isNaN(numKey) && numKey >= 0 && numKey <= 9 && this._pin.length < maxLength) {
                        this._pin.push(key);
                        this._focusedBoxIndex = this._pin.length;
                        this._updatePinBoxes();
                        this._updateSubmitButton();
                        this._hideError();

                        // Auto-focus submit when all digits entered (or 1 digit for age mode)
                        if (this._mode === "age" && this._pin.length >= 1) {
                            // For age, allow submit after 1 digit
                            this._setState("SubmitState");
                        } else if (this._pin.length === this._digitCount) {
                            // For PIN, require exact digit count
                            this._setState("SubmitState");
                        }
                        // return true;
                    }

                    // Handle backspace
                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._updateSubmitButton();
                            this._hideError();
                        }
                        // return true;
                    }

                    // return false;
                }
            },
            class CancelState extends this {
                override _getFocused() {
                    return this.Cancel;
                }

                override _handleRight() {
                    if (this._isVerifying) return;
                    // Only allow navigation to Submit if input is complete
                    let isComplete = false;
                    if (this._mode === "age") {
                        // Age can be 1 or 2 digits
                        isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
                    } else {
                        // PIN requires exact digit count
                        isComplete = this._pin.length === this._digitCount;
                    }
                    if (isComplete) {
                        this._setState("SubmitState");
                        // return true;
                    }
                    // return false;
                }

                override _handleUp() {
                    if (this._isVerifying) return;
                    // Go back to PIN boxes
                    this._setState("PinBoxesState");
                    // return true;
                }

                override _handleEnter() {
                    if (this._isVerifying) return;
                    return this._handleCancel();
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return;
                    const key = event.key;
                    // Handle backspace to go back to PIN input
                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._updateSubmitButton();
                            this._hideError();
                            this._setState("PinBoxesState");
                        }
                        // return true;
                    }
                    // return false;
                }
            },
            class SubmitState extends this {
                override _getFocused() {
                    // Only allow focusing Submit if input is complete
                    let isComplete = false;
                    if (this._mode === "age") {
                        // Age can be 1 or 2 digits
                        isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
                    } else {
                        // PIN requires exact digit count
                        isComplete = this._pin.length === this._digitCount;
                    }
                    if (isComplete) {
                        return this.Submit;
                    }
                    // Otherwise focus Cancel
                    return this.Cancel;
                }

                override _handleLeft() {
                    if (this._isVerifying) return;
                    this._setState("CancelState");
                    // return true;
                }

                override _handleUp() {
                    if (this._isVerifying) return;
                    // Go back to PIN boxes
                    this._setState("PinBoxesState");
                    // return true;
                }

                override _handleEnter() {
                    if (this._isVerifying) return;
                    // Only allow submit if input is complete
                    let isComplete = false;
                    if (this._mode === "age") {
                        // Age can be 1 or 2 digits
                        isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
                    } else {
                        // PIN requires exact digit count
                        isComplete = this._pin.length === this._digitCount;
                    }
                    if (isComplete) {
                        this._verifyPin();
                        // return true;
                    }
                    // return false;
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return;
                    const key = event.key;
                    // Handle backspace to go back to PIN input
                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._updateSubmitButton();
                            this._hideError();
                            this._setState("PinBoxesState");
                        }
                        // return true;
                    }
                    // return false;
                }
            }
        ];
    }

    override _getFocused() {
        // Default state - will be overridden by active state
        // Ensure we don't try to focus a box beyond digit count
        const boxIndex = Math.min(this._focusedBoxIndex, this._digitCount - 1);
        return this.PinBoxes.getByRef(`Box${boxIndex + 1}` as any);
    }

    override _handleDown() {
        if (this._isVerifying) return;
        // Always allow focusing Cancel button, even when PIN is not complete
        this._setState("CancelState");
        // return true;
    }

    override _handleUp() {
        // needed to prevent focus on page
    }

    override _handleRight() {
        // needed to prevent focus on page
    }

    override _handleEnter() {
        // needed to prevent focus on page
    }

    override _handleLeft() {
        // needed to prevent focus on page
    }

    override _handleBack() {
        Router.focusPage();
    }

    _updatePinBoxes() {
        const maxBoxes = this._digitCount;
        const boxSize = 80;
        const boxSpacing = 20;
        const totalWidth = boxSize * maxBoxes + boxSpacing * (maxBoxes - 1);

        // Update PinBoxes container width
        this.PinBoxes.patch({
            w: totalWidth
        });

        // For age mode, show numbers instead of asterisks
        const showNumbers = this._mode === "age";

        for (let i = 0; i < 4; i++) {
            const box = this.PinBoxes.getByRef(`Box${i + 1}` as any) as any;
            if (box) {
                const hasValue = i < this._pin.length;
                const shouldShow = i < maxBoxes;
                const mask = box.getByRef("Mask");
                const number = box.getByRef("Number");

                if (showNumbers && hasValue) {
                    // Show the actual number for age mode
                    if (mask) mask.patch({ alpha: 0 });
                    if (number) {
                        number.patch({
                            text: {
                                text: this._pin[i]
                            },
                            alpha: 1
                        });
                    }
                } else if (hasValue) {
                    // Show asterisk for PIN mode
                    if (mask) {
                        mask.patch({
                            alpha: 1
                        });
                    }
                    if (number) number.patch({ alpha: 0 });
                } else {
                    // No value - hide both
                    if (mask) mask.patch({ alpha: 0 });
                    if (number) number.patch({ alpha: 0 });
                }

                // Hide boxes beyond digit count
                box.patch({
                    alpha: shouldShow ? 1 : 0,
                    visible: shouldShow
                });
            }
        }
        // Update Submit button disabled state
        this._updateSubmitButton();
    }

    _updateSubmitButton() {
        // Disable Submit button if input is not complete
        // For age mode, allow 1 or 2 digits. For PIN, require exact digit count
        let isComplete = false;
        if (this._mode === "age") {
            // Age can be 1 or 2 digits
            isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
        } else {
            // PIN requires exact digit count
            isComplete = this._pin.length === this._digitCount;
        }
        this.Submit.disabled = !isComplete;
    }

    _focusSubmit() {
        this._setState("SubmitState");
        Router.focusWidget("PinInput");
        // Focus will be handled by _getFocused in SubmitState
    }

    _focusCancel() {
        this._setState("CancelState");
        Router.focusWidget("PinInput");
        // Focus will be handled by _getFocused in CancelState
    }

    _hideError() {
        this.Error.patch({ alpha: 0, key: "pin.enter" }); // Reset to default error message
    }

    _showError() {
        this.Error.patch({
            alpha: 1
        });
    }

    async _verifyPin() {
        // For age mode, allow 1 or 2 digits. For PIN, require exact digit count
        let isValidLength = false;
        if (this._mode === "age") {
            isValidLength = this._pin.length >= 1 && this._pin.length <= this._digitCount;
        } else {
            isValidLength = this._pin.length === this._digitCount;
        }

        if (!isValidLength) {
            this._showError();
            return;
        }

        this._isVerifying = true;
        const pin = this._pin.join("");

        // If mode is "set", store the PIN and ask for confirmation
        if (this._mode === "set") {
            this._isVerifying = false;
            this._firstPin = pin;
            // Clear PIN and switch to confirm mode
            this._pin = [];
            this._focusedBoxIndex = 0;
            this._updatePinBoxes();
            this._mode = "confirm";
            // Update title to show confirmation
            this.Title.patch({ key: "pin.confirmTitle" });
            this._hideError();
            this._setState("PinBoxesState");
            // Ensure widget stays focused and navigation works
            // Use setTimeout to ensure state change is complete before refocusing
            setTimeout(() => {
                Router.focusWidget("PinInput");
                this._refocus();
                // Explicitly focus the first box
                const firstBox = this.PinBoxes.getByRef("Box1");
                if (firstBox) {
                    firstBox._focus();
                }
            }, 50);
            return;
        }

        // If mode is "age", just return the age value
        if (this._mode === "age") {
            this._isVerifying = false;
            // Validate age (should be between 0 and 99)
            // Allow 1 or 2 digits
            const age = parseInt(pin, 10);
            if (isNaN(age) || age < 0 || age > 99) {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._updatePinBoxes();
                this.Error.patch({ key: "pin.invalidAge" });
                this._showError();
                this._setState("PinBoxesState");
                return;
            }
            // Age is valid, return it (pad with leading zero if single digit for consistency)
            const ageString = age < 10 ? `0${age}` : `${age}`;
            this._onPinSet?.(ageString);
            this._onSuccess?.();
            this.close();
            return;
        }

        // If mode is "confirm", compare with first PIN
        if (this._mode === "confirm") {
            this._isVerifying = false;
            if (pin === this._firstPin) {
                // PINs match, save it
                this._onPinSet?.(pin);
                this._onSuccess?.();
                this.close();
            } else {
                // PINs don't match, show error and restart
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._firstPin = undefined;
                this._mode = "set";
                this._updatePinBoxes();
                // Update error message to show mismatch
                this.Error.patch({ key: "pin.mismatch" });
                this._showError();
                // Update title back to set mode
                this.Title.patch({ key: "pin.setTitle" });
                this._setState("PinBoxesState");
            }
            return;
        }

        // Otherwise, verify the PIN
        try {
            let isValid = false;

            // Try API verification first
            // try {
            //     isValid = await verifyParentalPin(pin);
            // } catch (apiError) {
            //     console.error(
            //         "[PinInput] Error verifying PIN via API, falling back to platform-specific methods:",
            //         apiError
            //     );

            // Fallback to platform-specific APIs
            if (isPostTvLinux()) {
                // Use WebSocket to get PIN from settings and compare
                isValid = await new Promise<boolean>((resolve) => {
                    try {
                        // Timeout after 5 seconds
                        const timeoutId = setTimeout(() => {
                            resolve(false);
                        }, 5000);
                        postTvWebSocket.getSettings("pics.pin", (response) => {
                            clearTimeout(timeoutId);
                            try {
                                const settingsPin = response?.data?.value || response?.value || "";
                                resolve(settingsPin === pin);
                            } catch (e) {
                                console.error("[PinInput] Error parsing PIN response:", e);
                                resolve(false);
                            }
                        });
                    } catch (e) {
                        console.error("[PinInput] Error getting PIN from settings:", e);
                        resolve(false);
                    }
                });
            } else if (isPostTvAndroid()) {
                // Use Android.testPinCode
                try {
                    const android = window.Android;
                    if (android && typeof android.testPinCode === "function") {
                        const result = android.testPinCode(pin);
                        isValid = typeof result === "boolean" ? result : await Promise.resolve(result);
                    } else {
                        console.warn("[PinInput] Android.testPinCode not available");
                        isValid = false;
                    }
                } catch (e) {
                    console.error("[PinInput] Error calling Android.testPinCode:", e);
                    isValid = false;
                }
            } else {
                // Fallback: compare with stored PIN from user settings
                const user = Storage.get(STORAGE_KEYS.user) as any;
                const storedPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);
                isValid = !!storedPin && storedPin === pin;
            }
            // }

            this._isVerifying = false;

            if (isValid) {
                this.close();
                this._onSuccess?.();
            } else {
                // this._pin = [];
                // this._focusedBoxIndex = 0;
                // this._updatePinBoxes();
                this._showError();
            }
        } catch (e) {
            console.error("[PinInput] Error verifying PIN:", e);
            this._isVerifying = false;
            this._showError();
        }
    }

    override _focus() {
        this.patch({
            smooth: {
                alpha: 1
            }
        });
        this.Box.patch({
            smooth: {
                alpha: 1
            }
        });
    }

    override _unfocus() {
        this.patch({
            smooth: {
                alpha: 0
            }
        });
        this.Box.patch({
            smooth: {
                alpha: 0
            }
        });
    }

    open(
        onSuccess?: () => void,
        onCancel?: () => void,
        mode: "verify" | "set" | "confirm" | "age" | "options" = "verify",
        onPinSet?: (pin: string) => void,
        digitCount: number = 4,
        onTurnOff?: () => void
    ) {
        // Use pending callbacks if available, otherwise use passed callbacks
        if (pendingCallbacks) {
            this._onSuccess = pendingCallbacks.onSuccess;
            this._onCancel = pendingCallbacks.onCancel;
            clearPinInputCallbacks();
        } else {
            this._onSuccess = onSuccess;
            this._onCancel = onCancel;
        }
        this._mode = mode;
        this._onPinSet = onPinSet;
        this._onTurnOff = onTurnOff;
        this._digitCount = digitCount;
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._firstPin = undefined; // Reset first PIN

        // Show/hide appropriate UI elements based on mode
        if (mode === "options") {
            // Hide PIN boxes and actions, show options buttons
            if (this.PinBoxes) this.PinBoxes.patch({ alpha: 0, visible: false });
            if (this.Actions) this.Actions.patch({ alpha: 0, visible: false });
            if (this.Options) {
                (this.Options as any).patch({ alpha: 1, visible: true });
                if (this.ChangePin) this.ChangePin.patch({ alpha: 1, visible: true });
                if (this.TurnOff) this.TurnOff.patch({ alpha: 1, visible: true });
            }
            this._setState("OptionsState");
        } else {
            // Show PIN boxes and actions, hide options
            if (this.PinBoxes) this.PinBoxes.patch({ alpha: 1, visible: true });
            if (this.Actions) this.Actions.patch({ alpha: 1, visible: true });
            if (this.Options) {
                (this.Options as any).patch({ alpha: 0, visible: false });
                if (this.ChangePin) this.ChangePin.patch({ alpha: 0, visible: false });
                if (this.TurnOff) this.TurnOff.patch({ alpha: 0, visible: false });
            }
            this._setState("PinBoxesState");
            this._updatePinBoxes();
        }
        this._hideError();

        // Update title based on mode
        if (mode === "set") {
            this.Title.patch({ key: "pin.setTitle" });
        } else if (mode === "confirm") {
            this.Title.patch({ key: "pin.confirmTitle" });
        } else if (mode === "age") {
            this.Title.patch({ key: "pin.ageTitle" });
        } else if (mode === "options") {
            this.Title.patch({ key: "pin.optionsTitle" });
        } else {
            this.Title.patch({ key: "pin.title" });
        }

        // Make modal visible immediately (before focus)
        this.patch({
            alpha: 1,
            visible: true
        });
        this.Box.patch({
            alpha: 1,
            visible: true
        });

        // Focus the widget - this will trigger _focus() for smooth transitions
        Router.focusWidget("PinInput");

        // Ensure focus is set
        setTimeout(() => {
            this._focus();
            // Ensure the component receives focus
            this._refocus();

            // Focus appropriate element based on mode
            if (mode === "options") {
                // Use same refocus pattern as PIN boxes - OptionsState handles focus via _getFocused()
                this._refocus();
            } else {
                // Focus the first PIN box
                const firstBox = this.PinBoxes.getByRef("Box1");
                if (firstBox) {
                    firstBox._focus();
                }
            }
        }, 150);
    }

    close() {
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._mode = "verify";
        this._onPinSet = undefined;
        this._onTurnOff = undefined;
        this._firstPin = undefined;
        this._setState("PinBoxesState");

        // Reset UI visibility
        if (this.PinBoxes) this.PinBoxes.patch({ alpha: 1, visible: true });
        if (this.Options) (this.Options as any).patch({ alpha: 0, visible: false });
        if (this.Actions) this.Actions.patch({ alpha: 1, visible: true });

        Router.focusPage();
    }

    _handleChangePin() {
        // Callback to change PIN - will be handled by parent component
        this._onSuccess?.();
        this.close();
    }

    _handleTurnOff() {
        // Callback to turn off parental control
        this._onTurnOff?.();
        this.close();
    }

    getPin(): string {
        return this._pin.join("");
    }

    _handleCancel() {
        if (this._isVerifying) return false;
        this._onCancel?.();
        this.close();
        return true;
    }
}
